import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api, ApiError, qs } from '../lib/api';
import { useAuth } from '../lib/auth';
import { interimDeviceCode } from '../lib/deviceId';
import { Modal, TextInput } from '../components/ui';

interface SelfStatus {
  registered: boolean;
  name: string | null;
  /**
   * Whether this person is held to registering: the property's setting, never the
   * owner. Absent from a server that predates the setting, and then taken as true.
   */
  required?: boolean;
}

/**
 * Whether the given device is registered at the session's current property.
 * `deviceId` is `undefined` outside the Android kiosk build (or before a
 * session exists), which disables the query entirely - an ordinary browser
 * tab never calls this.
 */
export function useDeviceRegistration(deviceId: string | undefined) {
  return useQuery({
    queryKey: ['device-registration', deviceId],
    queryFn: () => api.get<SelfStatus>(`/api/sync/devices/self${qs({ deviceId: deviceId ?? '' })}`),
    enabled: Boolean(deviceId),
    staleTime: 60_000,
    // The app waits on this at start-up (useKioskAccess), so an unreachable
    // server has to fail promptly rather than after three backed-off retries.
    retry: false,
  });
}

/**
 * Blocks the kiosk build until its device is registered/pinned to a
 * property (Phase 6 device configuration model - docs/android/
 * PHASE-6-BASELINE-AUDIT.md SS3). App.tsx mounts this only when
 * isAndroidKiosk() is true and the device's own registration check comes
 * back unregistered - an ordinary browser session never renders it.
 */
/**
 * Registering this device as the property's terminal: the name, the call, and
 * what it says when that fails. Shared by the full-screen setup (below, where
 * registration is required) and the owner's own menu entry (RegisterTerminalModal,
 * where it is a choice).
 */
function useRegisterTerminal(deviceId: string, onRegistered: () => void) {
  const queryClient = useQueryClient();
  const deviceCode = interimDeviceCode(deviceId);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/sync/devices/register', {
        deviceId,
        deviceCode,
        name: name.trim(),
      });
      await queryClient.invalidateQueries({ queryKey: ['device-registration'] });
      onRegistered();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : 'Could not register this device. Try again.',
      );
    } finally {
      setBusy(false);
    }
  };

  return { name, setName, busy, error, submit, deviceCode };
}

/**
 * An owner choosing to register the device they are holding, from their account
 * menu. Registered, the device keeps its own copy of the till and works offline,
 * and is pinned to this property. Not offered to anybody else: registering is the
 * owner's, and staff on an unregistered device simply use the app online.
 */
export function RegisterTerminalModal({
  deviceId,
  onClose,
}: {
  deviceId: string;
  onClose: () => void;
}) {
  const { session } = useAuth();
  const form = useRegisterTerminal(deviceId, onClose);
  return (
    <Modal
      title="Register this device as a terminal"
      size="narrow"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={form.busy}>
            Cancel
          </button>
          <button
            className="btn primary"
            form="register-terminal"
            type="submit"
            disabled={form.busy || !form.name.trim()}
          >
            {form.busy ? 'Registering…' : 'Register this terminal'}
          </button>
        </>
      }
    >
      <form id="register-terminal" onSubmit={form.submit} className="stack">
        <p className="small muted">
          Registered, this device keeps its own copy of the till at{' '}
          <b>{session?.property?.name ?? 'this property'}</b> and keeps working without a
          connection. It will always open here - nobody signing in on it can switch it to another
          location. Until then you are using the app online, like the website.
        </p>
        <TextInput
          label="Terminal name"
          placeholder="e.g. Front Counter"
          value={form.name}
          onChange={(event) => form.setName(event.target.value)}
          error={form.error ?? undefined}
          required
          autoFocus
        />
        <div className="xs muted mono">Device code {form.deviceCode}</div>
      </form>
    </Modal>
  );
}

export function DeviceSetup({
  deviceId,
  onRegistered,
}: {
  deviceId: string;
  onRegistered: () => void;
}) {
  const { session, can, signOut } = useAuth();
  const { name, setName, busy, error, submit, deviceCode } = useRegisterTerminal(
    deviceId,
    onRegistered,
  );

  return (
    <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', padding: 24 }}>
      <div className="card" style={{ maxWidth: 420, width: '100%' }}>
        <div className="card-head">
          <h2>Set up this terminal</h2>
        </div>
        <div className="card-body stack">
          <p className="small muted">
            This tablet has not been registered at{' '}
            <b>{session?.property?.name ?? 'this property'}</b> yet. Once registered, it will
            always open here — nobody signing in on it will be able to switch it to another
            location.
          </p>

          {can('property:manage') ? (
            <form onSubmit={submit} className="stack">
              <TextInput
                label="Terminal name"
                placeholder="e.g. Front Counter"
                value={name}
                onChange={(event) => setName(event.target.value)}
                error={error ?? undefined}
                required
                autoFocus
              />
              <div className="xs muted mono">Device code {deviceCode}</div>
              <button className="btn primary" type="submit" disabled={busy || !name.trim()}>
                {busy ? 'Registering…' : 'Register this terminal'}
              </button>
            </form>
          ) : (
            <>
              <p className="small">
                Ask an administrator to sign in here and finish setup — a cashier account can't
                register a terminal.
              </p>
              <button className="btn" onClick={() => void signOut()}>
                Sign out
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
